import { z } from "zod";

/**
 * Wire contract for the patient Task status command
 * (`PUT /api/mobile/patient/{patientId}/tasks/{taskId}/status`).
 *
 * The web route parses the request with it and the mobile client parses the response with it, so
 * the two sides cannot drift. The caller sends the status it wants and the Task version it read.
 * The server owns the completion time.
 */

/** The two statuses a patient can set on a user Task: pending and completed. */
export const TASK_STATUS_COMMAND_VALUES = ["requested", "completed"] as const;

export type TaskStatusCommandValue = (typeof TASK_STATUS_COMMAND_VALUES)[number];

export const TaskStatusCommandRequestSchema = z.object({
  status: z.enum(TASK_STATUS_COMMAND_VALUES),
  // Version of the Task the caller read. A network retry sends the same value again.
  expectedVersionId: z.string().min(1),
});

export type TaskStatusCommandRequest = z.infer<typeof TaskStatusCommandRequestSchema>;

export const TaskStatusCommandResultSchema = z.object({
  id: z.string().min(1),
  status: z.string().min(1),
  versionId: z.string().min(1),
  // Null while the Task is not completed, and for historical completions with no recorded time.
  completedAt: z.string().nullable(),
});

export type TaskStatusCommandResult = z.infer<typeof TaskStatusCommandResultSchema>;

export const TaskStatusCommandResponseSchema = z.object({
  data: TaskStatusCommandResultSchema,
});

export type TaskStatusCommandResponse = z.infer<typeof TaskStatusCommandResponseSchema>;

/**
 * Machine-readable refusal code the status route returns when a form Task cannot be completed
 * because its questionnaire is still unsubmitted.
 *
 * Shared because the status route is not the only source of a 403 on this path: the Task
 * authorizer refuses a Task that is not the patient's, a patient with no verified practice, and an
 * inactive account with the same status. The app opens the form for this code alone, so an
 * unrelated refusal surfaces as an error instead of a form that then cannot be minted.
 */
export const TASK_FORM_REQUIRED_ERROR_CODE = "form_required";
